import type { MergeContactData, MergeFieldKey } from '@apparatix/types/Contacts';

export interface MergeFieldDefinition {
  key: MergeFieldKey;
  label: string;
  getValue: (contact: MergeContactData) => string;
}

export const mergeFieldDefinitions: MergeFieldDefinition[] = [
  { key: 'salutation', label: 'Salutation', getValue: contact => contact.salutation ?? '' },
  { key: 'first_name', label: 'First Name', getValue: contact => contact.firstName ?? '' },
  { key: 'middle_name', label: 'Middle Name', getValue: contact => contact.middleName ?? '' },
  { key: 'last_name', label: 'Last Name', getValue: contact => contact.lastName ?? '' },
  { key: 'suffix', label: 'Suffix', getValue: contact => contact.suffix ?? '' },
  { key: 'nickname', label: 'Nickname', getValue: contact => contact.nickname ?? '' },
  { key: 'job_title', label: 'Job Title', getValue: contact => contact.jobTitle ?? '' },
  { key: 'company_name', label: 'Company Name', getValue: contact => contact.companyName ?? '' },
  { key: 'birthday', label: 'Birthday', getValue: contact => contact.birthday ?? '' },
  { key: 'owner', label: 'Owner', getValue: contact => contact.owner?.fullName ?? '' },
];

// The fields the contact editor locks behind invoiceFieldsLocked; the merge wizard freezes the
// same set (plus address, emails, and phone numbers, which are handled separately).
export const mergeLockedFieldKeys: MergeFieldKey[] = ['first_name', 'middle_name', 'last_name'];

export interface MergeValueOption {
  id: number;
  label: string;
  addressType?: string;
  origin: 'source' | 'selected' | 'both';
  normalized: string;
}

interface ValueLike {
  id?: number;
  addressType?: string;
}

// Build the deduped union checkbox list for emails/phone numbers/websites. A value present on
// both contacts is represented by the source-owned id, so unchecking it detaches it from the
// survivor and the selected contact's twin is never attached.
export const buildMergeValueOptions = <T extends ValueLike>(
  sourceValues: T[] | undefined,
  selectedValues: T[] | undefined,
  getLabel: (value: T) => string,
  normalize: (label: string) => string,
): MergeValueOption[] => {
  const options: MergeValueOption[] = [];

  const append = (values: T[] | undefined, origin: 'source' | 'selected') => {
    values?.forEach(value => {
      const label = getLabel(value) ?? '';
      if (!value.id || !label.trim()) return;

      const normalized = normalize(label);
      const existing = options.find(option => option.normalized === normalized);
      if (existing) {
        if (origin === 'selected' && existing.origin === 'source') {
          existing.origin = 'both';
        }
        return;
      }

      options.push({
        id: value.id, label, addressType: value.addressType, origin, normalized,
      });
    });
  };

  append(sourceValues, 'source');
  append(selectedValues, 'selected');

  return options;
};

export const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const normalizePhoneNumber = (value: string): string => value.replace(/\D+/g, '');

export const normalizeUrl = (value: string): string => value.trim().toLowerCase();

export interface MergeSummary {
  fullName: string;
  fields: Array<{ key: MergeFieldKey; label: string; value: string }>;
  address: string;
  emails: MergeValueOption[];
  phoneNumbers: MergeValueOption[];
  webPages: MergeValueOption[];
  note: string;
}
